import React, { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useI18n } from "../i18n";
import { PLAN_META, formatXof, api } from "../api";
import { readReferralCode, clearReferralCode } from "../hooks/useReferralCapture";
import {
  AlertTriangle,
  Clock,
  Mail,
  ShieldCheck,
  CircleDollarSign,
  CreditCard,
  Smartphone,
  Coins,
  ArrowRight,
  ArrowLeft,
  Gift,
  CheckCircle2,
  XCircle,
} from "lucide-react";

// Payment methods metadata
// "mobilemoney" and "cards" both map to CinetPay backend with different channel filters
const PAYMENT_METHODS = [
  {
    id: "usdt",
    label_key: "checkout.payment_usdt",
    desc_key: "checkout.payment_usdt_desc",
    icon: Coins,
    available: true,
  },
  {
    id: "mobilemoney",
    label: "Mobile Money",
    desc: "Orange Money · MTN MoMo · Moov · Wave",
    icon: Smartphone,
    available: true,
  },
  {
    id: "cards",
    label: "Cartes bancaires",
    desc: "Visa · Mastercard · cartes prépayées",
    icon: CreditCard,
    available: true,
  },
];

// Payment provider logos (PNG composites stored in /public/assets/payment/)
const LOGO_USDT = "/assets/payment/usdt-trc20.png";
const LOGO_MOBILEMONEY = "/assets/payment/mobile-money.png";
const LOGO_CARDS = "/assets/payment/cards.png";

function PaymentLogo({ src, alt }) {
  return (
    <div className="mt-2.5">
      <img
        src={src}
        alt={alt}
        className="h-9 sm:h-10 w-auto"
        loading="lazy"
        draggable="false"
      />
    </div>
  );
}

const STORAGE_KEY = "dtsp_pending_order";

export default function Checkout() {
  const { planId } = useParams();
  const { t, lang } = useI18n();
  const navigate = useNavigate();

  const meta = PLAN_META[planId];
  const tier = meta?.tier;

  // Pre-fill from sessionStorage (in case user came back from page 2)
  const stored = useMemo(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const obj = JSON.parse(raw);
      return obj.plan_id === planId ? obj : null;
    } catch {
      return null;
    }
  }, [planId]);

  const [fullName, setFullName] = useState(stored?.full_name || "");
  const [email, setEmail] = useState(stored?.email || "");
  const [phone, setPhone] = useState(stored?.phone || "");
  const [broker, setBroker] = useState(stored?.broker || "");
  const [referralCode, setReferralCode] = useState(() =>
    (stored?.referral_code || readReferralCode() || "").toUpperCase()
  );
  const [resolvedCode, setResolvedCode] = useState(null); // { valid, type, promo_type?, discount_percent?, discount_xof_preview?, final_price_xof?, affiliate_name?, reason? }
  const [linkedAffiliate, setLinkedAffiliate] = useState(null);
  const initAccounts =
    tier === "single" ? ["", ""] : tier === "multi" ? ["", "", "", "", "", ""] : [];
  const [accounts, setAccounts] = useState(() => {
    if (!stored?.mt5_accounts) return initAccounts;
    const merged = [...initAccounts];
    stored.mt5_accounts.forEach((v, i) => {
      if (i < merged.length) merged[i] = v;
    });
    return merged;
  });
  const [payment, setPayment] = useState(stored?.ui_method || stored?.payment_method || "usdt");
  const [error, setError] = useState("");
  const [redirecting, setRedirecting] = useState(false);

  // Live-resolve the code (debounced). Handles both referral and promo.
  useEffect(() => {
    const c = referralCode.trim().toUpperCase();
    if (!c) { setResolvedCode(null); return; }
    if (c.length < 3) { setResolvedCode(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/codes/resolve", {
          params: { code: c, plan_id: planId, email: email.trim().toLowerCase() },
        });
        setResolvedCode(data);
      } catch {
        setResolvedCode({ valid: false, reason: "error", code: c });
      }
    }, 400);
    return () => clearTimeout(t);
  }, [referralCode, planId, email]);

  // Detect renewal: is this email already linked to an affiliate?
  useEffect(() => {
    const e = email.trim().toLowerCase();
    if (!e || !/^\S+@\S+\.\S+$/.test(e)) { setLinkedAffiliate(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/affiliates/client-link-status`, { params: { email: e } });
        setLinkedAffiliate(data.linked ? data : null);
      } catch {
        setLinkedAffiliate(null);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [email]);

  const labelDuration = {
    "1m": t("pricing.duration_1m"),
    "3m": t("pricing.duration_3m"),
    "6m": t("pricing.duration_6m"),
    "12m": t("pricing.duration_12m"),
  };

  if (!meta) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="page-checkout-404">
        <h1 className="text-3xl font-bold mb-4">Plan introuvable</h1>
        <Link to="/pricing" className="btn-primary inline-block mt-4">
          ← Voir les tarifs
        </Link>
      </div>
    );
  }

  const updateAccount = (idx, val) => {
    const digitsOnly = val.replace(/\D/g, "");
    const next = [...accounts];
    next[idx] = digitsOnly;
    setAccounts(next);
  };

  const validate = () => {
    if (!fullName.trim()) return t("checkout.full_name") + " ?";
    if (!/^\S+@\S+\.\S+$/.test(email)) return t("checkout.email") + " ?";
    if (!phone.trim() || phone.replace(/\D/g, "").length < 4)
      return t("checkout.phone") + " ?";
    if (tier !== "unlimited") {
      const filled = accounts.map((a) => a.trim()).filter(Boolean);
      if (filled.length < 1) return t("checkout.mt5_accounts_label") + " ?";
      for (const a of filled) {
        if (!/^\d+$/.test(a)) return `${t("checkout.mt5_digits_only")} — "${a}"`;
      }
    }
    if (!payment) return t("checkout.method") + " ?";
    return null;
  };

  const onContinue = async () => {
    setError("");
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    const cinetpayChannel =
      payment === "mobilemoney" ? "MOBILE_MONEY" :
      payment === "cards" ? "CREDIT_CARD" : "";
    const backendMethod = (payment === "mobilemoney" || payment === "cards") ? "cinetpay" : payment;

    const data = {
      plan_id: planId,
      full_name: fullName.trim(),
      email: email.trim(),
      phone: phone.trim(),
      broker: broker.trim(),
      mt5_accounts: tier === "unlimited" ? [] : accounts.map((a) => a.trim()).filter(Boolean),
      payment_method: backendMethod,
      cinetpay_channel: cinetpayChannel,
      ui_method: payment,
      referral_code: linkedAffiliate ? "" : referralCode.trim().toUpperCase(),
      lang,
    };

    // Mobile Money / Cartes → redirection directe vers CinetPay (pas de page intermédiaire)
    if (backendMethod === "cinetpay") {
      setRedirecting(true);
      try {
        const submitPayload = {
          plan_id: planId,
          full_name: data.full_name,
          email: data.email,
          phone: data.phone,
          broker: data.broker,
          mt5_accounts: data.mt5_accounts,
          payment_method: "cinetpay",
          payment_reference: "pending-cinetpay",
          referral_code: data.referral_code,
          lang,
        };
        const sub = await api.post("/orders/submit", submitPayload);
        const orderId = sub.data.order_id;
        const fd = new FormData();
        fd.append("order_id", orderId);
        if (cinetpayChannel) fd.append("channels", cinetpayChannel);
        const init = await api.post("/payments/cinetpay/initialize", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (init.data?.payment_url) {
          clearReferralCode();
          sessionStorage.removeItem(STORAGE_KEY);
          window.location.href = init.data.payment_url;
          return;
        }
        throw new Error("payment_url manquant");
      } catch (e) {
        setRedirecting(false);
        setError(e?.response?.data?.detail || "Erreur d'initialisation du paiement. Réessayez ou contactez le support.");
        return;
      }
    }

    // Autres méthodes (USDT) → page 2 classique
    // Side effect : crée un brouillon de commande côté backend pour que
    // la relance "panier abandonné" puisse cibler ce visiteur s'il ne
    // colle jamais son hash de transaction. Best-effort — non bloquant.
    try {
      await api.post("/orders/usdt-draft", {
        plan_id: planId,
        full_name: data.full_name,
        email: data.email,
        phone: data.phone,
        broker: data.broker,
        mt5_accounts: data.mt5_accounts,
        referral_code: data.referral_code,
        lang,
      });
    } catch (e) {
      // Draft is non-blocking: payment flow must continue even if draft save fails.
      // Log for diagnostics in case of recurring backend issues.
      console.warn("[checkout] draft save failed (non-blocking):", e?.response?.status, e?.response?.data?.detail);
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    navigate(`/checkout/${planId}/payment`);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12" data-testid="page-checkout">
      {/* Stepper */}
      <div className="mb-6 flex items-center gap-3 text-xs font-mono">
        <span className="text-cyan-300 font-semibold">1. {t("checkout.step_info")}</span>
        <span className="text-zinc-600">→</span>
        <span className="text-zinc-500">2. {t("checkout.step_payment")}</span>
      </div>
      <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tighter">
          {t("checkout.title")}
        </h1>
        <Link
          to="/pricing"
          className="shrink-0 text-xs text-zinc-400 hover:text-cyan-300 inline-flex items-center gap-1.5"
          data-testid="checkout-back-to-pricing"
        >
          <ArrowLeft className="h-4 w-4" /> {t("checkout.back_to_pricing")}
        </Link>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-8">
        <div className="space-y-6">
          {/* 1. Customer info */}
          <section className="glass rounded-2xl p-6">
            <h2 className="text-lg font-semibold mb-1">{t("checkout.step_info")}</h2>
            <div className="text-xs text-zinc-500 mb-5">{t("common.required")}</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-zinc-400 mb-1.5">
                  {t("checkout.full_name")} *
                </label>
                <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)}
                       className="input" data-testid="checkout-full-name" />
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1.5">
                  {t("checkout.email")} *
                </label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                       className="input" data-testid="checkout-email" />
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1.5">
                  {t("checkout.phone")} *
                </label>
                <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                       className="input" data-testid="checkout-phone" />
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1.5">
                  {t("checkout.broker")}
                </label>
                <input type="text" value={broker} onChange={(e) => setBroker(e.target.value)}
                       className="input" placeholder="ICMarkets, Exness…" data-testid="checkout-broker" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs text-zinc-400 mb-1.5 flex items-center gap-1.5">
                  <Gift className="h-3.5 w-3.5 text-cyan-300" /> {t("checkout.promo_label")}
                </label>

                {linkedAffiliate ? (
                  <div className="p-3 bg-emerald-400/5 border border-emerald-400/30 rounded-lg flex items-start gap-2" data-testid="checkout-renewal-banner">
                    <CheckCircle2 className="h-4 w-4 text-emerald-300 shrink-0 mt-0.5" />
                    <div className="text-xs leading-relaxed">
                      <div className="text-emerald-200 font-medium mb-0.5">
                        Vous êtes déjà lié au parrain <b>{linkedAffiliate.affiliate_name || linkedAffiliate.affiliate_code}</b>
                      </div>
                      <div className="text-zinc-400">
                        Il s'agit d'un <b>renouvellement</b>. La commission sera automatiquement créditée à votre parrain initial — les codes promo/parrainage sont inutiles ici.
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <input type="text" value={referralCode}
                        onChange={(e) => setReferralCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ""))}
                        className="input uppercase font-mono tracking-wider"
                        placeholder="DTSP-XXXXXX ou PROMO-XXXXXX"
                        maxLength={30}
                        data-testid="checkout-referral" />
                      {resolvedCode?.valid && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-emerald-300 inline-flex items-center gap-1"
                          data-testid="checkout-referral-valid">
                          <CheckCircle2 className="h-3 w-3" /> Valide
                        </span>
                      )}
                      {resolvedCode && !resolvedCode.valid && referralCode.length >= 3 && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-red-300 inline-flex items-center gap-1"
                          data-testid="checkout-referral-invalid">
                          <XCircle className="h-3 w-3" />
                          {resolvedCode.reason === "new_only" ? "Nouveaux clients uniquement"
                            : resolvedCode.reason === "expired_or_disabled" ? "Expiré"
                            : "Code inconnu"}
                        </span>
                      )}
                    </div>

                    {resolvedCode?.valid && resolvedCode.type === "promo" && (
                      <div className="mt-2 p-3 bg-cyan-400/5 border border-cyan-400/30 rounded-lg text-xs" data-testid="checkout-discount-preview">
                        <div className="text-cyan-200 font-medium mb-0.5">
                          Réduction de {resolvedCode.discount_percent}% appliquée
                          {resolvedCode.promo_type === "affiliate" && resolvedCode.affiliate_name && (
                            <> (code de <b>{resolvedCode.affiliate_name}</b>)</>
                          )}
                        </div>
                        {resolvedCode.discount_xof_preview != null && (
                          <div className="text-zinc-400">
                            − {formatXof(resolvedCode.discount_xof_preview)} XOF →
                            <b className="text-emerald-300 ml-1">{formatXof(resolvedCode.final_price_xof)} XOF</b>
                          </div>
                        )}
                      </div>
                    )}

                    {resolvedCode?.valid && resolvedCode.type === "referral" && (
                      <div className="mt-2 text-xs text-emerald-300" data-testid="checkout-referral-info">
                        Parrainé par <b>{resolvedCode.affiliate_name || resolvedCode.code}</b> ✓
                      </div>
                    )}

                    {!referralCode && (
                      <div className="text-[11px] text-zinc-500 mt-1">Vous avez un code ? Saisissez-le ici pour une réduction ou pour créditer votre parrain.</div>
                    )}
                  </>
                )}
              </div>
            </div>
          </section>

          {/* 2. MT5 accounts */}
          <section className="glass rounded-2xl p-6">
            <h2 className="text-lg font-semibold mb-1">{t("checkout.mt5_accounts_label")}</h2>
            <p className="text-xs text-zinc-500 mb-5">
              {tier === "single" && t("checkout.mt5_help_single")}
              {tier === "multi" && t("checkout.mt5_help_multi")}
              {tier === "unlimited" && t("checkout.mt5_help_unlimited")}
            </p>

            {tier === "unlimited" ? (
              <div className="text-sm text-amber-300 bg-amber-400/5 border border-amber-400/20 rounded-xl p-4 flex items-start gap-3">
                <ShieldCheck className="h-5 w-5 shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold mb-1">Licence Compte Illimité</div>
                  <div className="text-zinc-300">{t("checkout.mt5_help_unlimited")}</div>
                </div>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {accounts.map((acc, idx) => {
                  const hint =
                    tier === "single" && idx === 0 ? t("checkout.mt5_demo")
                    : tier === "single" && idx === 1 ? t("checkout.mt5_live")
                    : `${t("checkout.mt5_account_n")}${idx + 1}`;
                  return (
                    <div key={idx}>
                      <label className="block text-xs text-zinc-400 mb-1.5">
                        {hint} {idx === 0 && tier === "single" && "*"}
                      </label>
                      <input type="text" inputMode="numeric" pattern="[0-9]*"
                             value={acc} onChange={(e) => updateAccount(idx, e.target.value)}
                             className="input" placeholder="ex: 100934634"
                             data-testid={`checkout-mt5-${idx}`} />
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* 3. Payment method */}
          <section className="glass rounded-2xl p-6">
            <h2 className="text-lg font-semibold mb-5">{t("checkout.method")}</h2>
            <div className="grid gap-3">
              {PAYMENT_METHODS.map((m) => {
                const Icon = m.icon;
                const active = payment === m.id;
                return (
                  <button key={m.id} type="button" disabled={!m.available}
                    onClick={() => m.available && setPayment(m.id)}
                    className={`flex items-center gap-4 p-4 rounded-xl border transition-all text-left ${
                      active
                        ? "border-cyan-400 bg-cyan-400/5"
                        : m.available
                          ? "border-white/[0.08] hover:border-white/20 bg-white/[0.02]"
                          : "border-white/[0.04] bg-white/[0.02] opacity-50 cursor-not-allowed"
                    }`}
                    data-testid={`checkout-payment-${m.id}`}>
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      active ? "bg-cyan-400 text-[#05050A]" : "bg-white/[0.05] text-zinc-300"
                    }`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-semibold flex items-center gap-2">
                        {m.label_key ? t(m.label_key) : m.label}
                        {!m.available && (
                          <span className="text-[10px] px-2 py-0.5 rounded bg-amber-400/10 text-amber-300 border border-amber-400/20">
                            {t("checkout.payment_coming_soon")}
                          </span>
                        )}
                      </div>
                      {m.id === "mobilemoney" ? (
                        <PaymentLogo src={LOGO_MOBILEMONEY} alt="Orange Money · Moov Money · MTN Mobile Money · Wave" />
                      ) : m.id === "cards" ? (
                        <PaymentLogo src={LOGO_CARDS} alt="Visa · Mastercard" />
                      ) : m.id === "usdt" ? (
                        <PaymentLogo src={LOGO_USDT} alt="Tether USDT — TRX TRC20" />
                      ) : (
                        <div className="text-xs text-zinc-500 mt-0.5">
                          {m.desc_key ? t(m.desc_key) : m.desc}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-300 rounded-lg px-4 py-3 text-sm flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}

          <button onClick={onContinue} disabled={redirecting}
            className="btn-primary w-full py-4 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-wait"
            data-testid="checkout-continue">
            {t("checkout.continue_to_payment")} <ArrowRight className="h-4 w-4" />
          </button>
        </div>

        {/* RIGHT — Summary */}
        <aside className="lg:sticky lg:top-24 h-fit">
          <div className="glass rounded-2xl p-6">
            <div className="text-xs uppercase tracking-[0.15em] text-zinc-500 mb-3">
              {t("checkout.summary")}
            </div>
            <div className="text-sm text-zinc-400 mb-1">{t(`pricing.${tier}`)}</div>
            <div className="text-xl font-semibold mb-3">{labelDuration[meta.duration]}</div>
            <div className="flex items-baseline gap-1 mb-1">
              <div className={`text-3xl font-bold tracking-tighter font-mono ${resolvedCode?.valid && resolvedCode.type === "promo" ? "line-through text-zinc-500 text-xl" : ""}`}>
                {formatXof(meta.xof)}
              </div>
              <div className="text-sm text-zinc-400">{t("common.currency")}</div>
            </div>
            {resolvedCode?.valid && resolvedCode.type === "promo" && resolvedCode.final_price_xof != null && (
              <div className="flex items-baseline gap-1 mb-1" data-testid="checkout-summary-final">
                <div className="text-3xl font-bold tracking-tighter font-mono text-emerald-300">
                  {formatXof(resolvedCode.final_price_xof)}
                </div>
                <div className="text-sm text-zinc-400">{t("common.currency")}</div>
                <div className="text-[10px] uppercase font-mono bg-emerald-400/15 text-emerald-300 px-1.5 py-0.5 rounded ml-2">
                  −{resolvedCode.discount_percent}%
                </div>
              </div>
            )}
            <div className="text-xs text-zinc-500 mb-5">
              ≈ ${meta.usd.toLocaleString("en-US")} USD
            </div>
            <div className="space-y-2 text-xs text-zinc-400">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-cyan-400" />
                {t("checkout.delivery_time")}
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-cyan-400" />
                {t("checkout.delivery_email")}
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-cyan-400" />
                {t("checkout.delivery_license")}
              </div>
            </div>
          </div>
        </aside>
      </div>

      {/* Full-screen redirection overlay */}
      {redirecting && (
        <div className="fixed inset-0 z-50 bg-[#05050A]/85 backdrop-blur-sm flex items-center justify-center px-4" data-testid="checkout-redirecting">
          <div className="glass rounded-2xl p-8 max-w-md w-full text-center">
            <div className="relative w-20 h-20 mx-auto mb-5 flex items-center justify-center">
              <span className="absolute inset-0 rounded-full border-2 border-cyan-400/40 animate-ping" />
              <span className="absolute inset-2 rounded-full border-2 border-cyan-400/30 animate-ping" style={{ animationDelay: "300ms" }} />
              <div className="relative w-14 h-14 rounded-full bg-cyan-400/15 border border-cyan-400/40 text-cyan-300 flex items-center justify-center">
                <svg className="h-7 w-7 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeDasharray="50 12" strokeLinecap="round" />
                </svg>
              </div>
            </div>
            <div className="text-base font-semibold mb-2">
              Redirection vers CinetPay…
            </div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-3">
              Page de paiement sécurisée
            </div>
            <div className="text-xs text-zinc-400 leading-relaxed">
              Ne fermez pas cette fenêtre. Vous allez être redirigé automatiquement vers la page de paiement officielle de notre partenaire <b>CinetPay</b>…
            </div>
            <div className="mt-5 w-full h-1 rounded-full overflow-hidden bg-white/[0.05]">
              <div
                className="h-full w-1/3 bg-gradient-to-r from-transparent via-cyan-400 to-transparent"
                style={{ animation: "slide 1.4s ease-in-out infinite" }}
              />
            </div>
            <style>{`@keyframes slide { 0% { transform: translateX(-100%);} 100% { transform: translateX(400%);} }`}</style>
          </div>
        </div>
      )}
    </div>
  );
}
